import { useEffect, useState } from 'react';

/**
 * Debounce a changing value by a specified delay.
 *
 * @template T
 * @param {T} value - The value to debounce.
 * @param {number} [delay=300] - The delay in milliseconds. If delay is 0 or falsey, the value updates immediately.
 * @returns {T} The debounced value.
 */
export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    if (!delay) {
      setDebouncedValue(value);
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
